/**
 * 🦔 Armadillo Adamantium Security Provider
 * Wraps the entire app with 10-layer real-time protection.
 * 
 * Usage: Wrap <App /> with <ArmadilloProvider>
 * Then call useSecurity() in any component
 */

import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { armadilloClient, runSecurityPipeline, ArmadilloResult, initializeArmadillo } from '@/security/armadillo-bridge';
import type { TrustTierName } from '@/security/armadillo/types';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

// ============================================
// CONTEXT
// ============================================

interface SecurityState {
  initialized: boolean;
  trustTier: TrustTierName;
  riskScore: number;
  eventsBlocked: number;
  threatsDetected: number;
  sessionId: string;
}

interface ArmadilloContextType extends SecurityState {
  validateAction: (action: string, input?: string) => Promise<ArmadilloResult>;
  quickCheck: (input: string) => { valid: boolean; reason?: string };
  logSecurityEvent: (eventType: string, severity: 'low' | 'medium' | 'high' | 'critical', message: string, metadata?: Record<string, any>) => Promise<void>;
  refreshState: () => void;
  securityStats: {
    totalPipelines: number;
    blocked: number;
    allowed: number;
    lastCheck: string | null;
  };
}

const ArmadilloContext = createContext<ArmadilloContextType | null>(null);

// ============================================
// PROVIDER
// ============================================

export function ArmadilloProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [state, setState] = useState<SecurityState>({
    initialized: false,
    trustTier: 'untrusted',
    riskScore: 0,
    eventsBlocked: 0,
    threatsDetected: 0,
    sessionId: ''
  });

  const [pipelines, setPipelines] = useState({ total: 0, blocked: 0, allowed: 0 });
  const [lastCheck, setLastCheck] = useState<string | null>(null);

  // Initialize on mount — deferred until the browser is idle so the security
  // stack (crypto handshake, fingerprinting, monitors) never competes with
  // first paint on low-end devices. requestIdleCallback is skipped in
  // background/hidden tabs, so fall back to a short timeout there.
  useEffect(() => {
    let cancelled = false;
    const scheduleIdle = (cb: () => void) => {
      const ric = (window as any).requestIdleCallback;
      if (typeof ric === 'function') {
        ric(cb, { timeout: 2500 });
      } else {
        setTimeout(cb, 2500);
      }
    };

    const init = async () => {
      try {
        await initializeArmadillo(user?.id);
        
        // Build context for session tracking
        const ctx = await armadilloClient.buildContext(user?.id || null, 'init');
        
        if (cancelled) return;

        setState(s => ({
          ...s,
          initialized: true,
          sessionId: ctx.sessionId,
          trustTier: 'verified'
        }));
        

      } catch (e) {

        if (!cancelled) setState(s => ({ ...s, initialized: true }));
      }
    };
    const timer = setTimeout(() => {
      if (!cancelled) scheduleIdle(init);
    }, 1200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [user?.id]);

  // Periodically cleanup expired threat capsules
  useEffect(() => {
    const interval = setInterval(() => {
      // Safely call cleanup with null checks for both client and method
      if (armadilloClient && typeof armadilloClient.cleanupExpiredCapsules === 'function') {
        try {
          // Cast to any to handle both sync (void) and async (Promise) return types
          const result = armadilloClient.cleanupExpiredCapsules() as any;
          if (result && typeof result.then === 'function') {
            result.catch(() => {});
          }
        } catch {
          // Silently ignore cleanup errors
        }
      }
    }, 60000); // Every 60 seconds
    return () => clearInterval(interval);
  }, []);

  const validateAction = useCallback(async (action: string, input?: string): Promise<ArmadilloResult> => {
    setPipelines(p => ({ ...p, total: p.total + 1 }));
    setLastCheck(new Date().toISOString());

    const result = await runSecurityPipeline(user?.id || null, action, input);

    if (!result.allowed) {
      setPipelines(p => ({ ...p, blocked: p.blocked + 1 }));
      setState(s => ({ ...s, eventsBlocked: s.eventsBlocked + 1 }));

      if (result.layerBlocked === 'L4-TCN') {
        setState(s => ({ ...s, threatsDetected: s.threatsDetected + 1 }));
        toast.error('Security alert: Malicious input blocked');
        
        // Log threat to database
        if (user?.id) {
          await logSecurityEventToDB(
            user.id,
            'threat_blocked',
            'high',
            `Threat blocked by ${result.layerBlocked}: ${result.reason || 'Malicious input detected'}`,
            { action, inputSnippet: input?.substring(0, 100), layer: result.layerBlocked }
          );
        }
      } else {
        // Log other blocks (rate limit, policy, etc.)
        if (user?.id) {
          await logSecurityEventToDB(
            user.id,
            'action_blocked',
            result.layerBlocked === 'L3-ARE' ? 'medium' : 'low',
            `Action blocked by ${result.layerBlocked}: ${result.reason || 'Security policy violation'}`,
            { action, layer: result.layerBlocked }
          );
        }
      }
    } else {
      setPipelines(p => ({ ...p, allowed: p.allowed + 1 }));
    }

    if (result.trustTier) {
      setState(s => ({ ...s, trustTier: result.trustTier!, riskScore: result.riskScore || s.riskScore }));
    }

    return result;
  }, [user?.id]);

  const quickCheck = useCallback((input: string) => {
    return armadilloClient.quickValidate(input);
  }, []);

  // Log security event to Supabase via the server API (captures the real IP
  // server-side); falls back to a direct insert if the API is unreachable.
  const logSecurityEventToDB = useCallback(async (
    userId: string,
    eventType: string,
    severity: 'low' | 'medium' | 'high' | 'critical',
    message: string,
    metadata?: Record<string, any>
  ) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      try {
        const res = await fetch(`/api/armadillo?action=log-event`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ eventType, severity, message, metadata }),
        });
        if (res.ok) return;
      } catch {
        // fall through to direct insert
      }
    }
    try {
      const { error } = await supabase
        .from('security_events')
        .insert({
          user_id: userId,
          event_type: eventType,
          severity,
          message,
          metadata: metadata || {},
          ip_address: 'client-side', // Will be populated by server-side middleware in future
          user_agent: navigator.userAgent
        });

      if (error) {

      } else {

      }
    } catch (err) {

    }
  }, []);

  // Public method to log security events from any component
  const logSecurityEvent = useCallback(async (
    eventType: string,
    severity: 'low' | 'medium' | 'high' | 'critical',
    message: string,
    metadata?: Record<string, any>
  ) => {
    if (!user?.id) {

      return;
    }
    
    await logSecurityEventToDB(user.id, eventType, severity, message, metadata);
  }, [user?.id, logSecurityEventToDB]);

  const refreshState = useCallback(() => {
    // Force re-render to update UI
    setState(s => ({ ...s }));
  }, []);

  const value: ArmadilloContextType = {
    ...state,
    validateAction,
    quickCheck,
    logSecurityEvent,
    refreshState,
    securityStats: {
      totalPipelines: pipelines.total,
      blocked: pipelines.blocked,
      allowed: pipelines.allowed,
      lastCheck
    }
  };

  return (
    <ArmadilloContext.Provider value={value}>
      {children}
    </ArmadilloContext.Provider>
  );
}

// ============================================
// HOOK
// ============================================

export function useSecurity() {
  const ctx = useContext(ArmadilloContext);
  if (!ctx) {
    throw new Error('useSecurity must be used within <ArmadilloProvider>');
  }
  return ctx;
}

// ============================================
// SECURITY BADGE COMPONENT
// ============================================

export function SecurityStatusBadge() {
  const { trustTier, initialized, threatsDetected, eventsBlocked } = useSecurity();

  if (!initialized) return null;

  const colors: Record<TrustTierName, string> = {
    untrusted: 'bg-red-500',
    probationary: 'bg-yellow-500',
    verified: 'bg-blue-500',
    trusted: 'bg-green-500',
    elevated: 'bg-purple-500',
    armadillo: 'bg-emerald-600'
  };

  return (
    <div className="flex items-center gap-2">
      <span className={`inline-block w-2 h-2 rounded-full ${colors[trustTier] || 'bg-gray-500'}`}
            title={`Trust Tier: ${trustTier}`} />
      <span className="text-xs text-muted-foreground hidden sm:inline">
        🦔 Armadillo {trustTier === 'armadillo' ? '🔒' : '🛡️'}
      </span>
      {threatsDetected > 0 && (
        <span className="text-xs text-red-500 font-bold" title={`${threatsDetected} threats blocked`}>
          ⚠️ {threatsDetected}
        </span>
      )}
    </div>
  );
}
